import { useCallback } from "react";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import {
  SelfServeEventName,
  SelfServeRawEventName,
  SupportHubRawEventName,
  type SelfServeHTSEvent,
  type SelfServeRawEvent,
  type SupportHubHTSEvent,
  type SupportHubRawEvent,
} from "./types";

const SELF_SERVE_EVENT_NAMES = {
  [SelfServeRawEventName.ConfirmResendEmail]:
    SelfServeEventName.confirm_resend_email,
  [SelfServeRawEventName.SubmittedQuery]: SelfServeEventName.submitted_query,
  [SelfServeRawEventName.TappedSuggestionChip]:
    SelfServeEventName.tapped_suggestion_chip,
  [SelfServeRawEventName.ViewedResendEmailModal]:
    SelfServeEventName.viewed_resend_email,
} as const satisfies Record<SelfServeRawEventName, SelfServeHTSEvent["name"]>;

const SUPPORT_HUB_EVENT_NAMES = {
  [SupportHubRawEventName.SupportHubHelpLaunched]:
    SelfServeEventName.support_hub_help_launched,
  [SupportHubRawEventName.SupportHubViewed]:
    SelfServeEventName.support_hub_viewed,
  [SupportHubRawEventName.SupportHubTappedManageBooking]:
    SelfServeEventName.support_hub_tapped_manage_booking,
  [SupportHubRawEventName.SupportHubTappedFaq]:
    SelfServeEventName.support_hub_tapped_faq,
  [SupportHubRawEventName.SupportHubTappedContactSupport]:
    SelfServeEventName.support_hub_tapped_contact_support,
  [SupportHubRawEventName.SupportHubDismissed]:
    SelfServeEventName.support_hub_dismissed,
} as const satisfies Record<SupportHubRawEventName, SupportHubHTSEvent["name"]>;

type SelfServeNonHubRawEvent = Exclude<SelfServeRawEvent, SupportHubRawEvent>;
type SelfServeNonHubHTSEvent = Exclude<SelfServeHTSEvent, SupportHubHTSEvent>;

const useSelfServeTransformer: UseInstantTransformEvent<
  SelfServeNonHubRawEvent,
  SelfServeNonHubHTSEvent
> = (name) =>
  useCallback(
    (event) =>
      ({
        name: SELF_SERVE_EVENT_NAMES[name],
        properties: event.properties,
      }) as SelfServeNonHubHTSEvent,
    [name]
  );

const useSupportHubTransformer: UseInstantTransformEvent<
  SupportHubRawEvent,
  SupportHubHTSEvent
> = (name) =>
  useCallback(
    (event) =>
      ({
        name: SUPPORT_HUB_EVENT_NAMES[name],
        properties: event.properties,
      }) as SupportHubHTSEvent,
    [name]
  );

export const SELF_SERVE_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  SelfServeRawEvent,
  SelfServeHTSEvent
> = {
  [SelfServeRawEventName.ConfirmResendEmail]: [useSelfServeTransformer],
  [SelfServeRawEventName.SubmittedQuery]: [useSelfServeTransformer],
  [SelfServeRawEventName.TappedSuggestionChip]: [useSelfServeTransformer],
  [SelfServeRawEventName.ViewedResendEmailModal]: [useSelfServeTransformer],
  [SupportHubRawEventName.SupportHubHelpLaunched]: [useSupportHubTransformer],
  [SupportHubRawEventName.SupportHubViewed]: [useSupportHubTransformer],
  [SupportHubRawEventName.SupportHubTappedManageBooking]: [
    useSupportHubTransformer,
  ],
  [SupportHubRawEventName.SupportHubTappedFaq]: [useSupportHubTransformer],
  [SupportHubRawEventName.SupportHubTappedContactSupport]: [
    useSupportHubTransformer,
  ],
  [SupportHubRawEventName.SupportHubDismissed]: [useSupportHubTransformer],
};
